import { memo, useEffect, useRef } from 'react'
import { BookOpen, ExternalLink } from 'lucide-react'
import type { Source } from '@/api/types'
import { sourceNumber } from '@/lib/citations'
import { cn } from '@/lib/cn'
import { prefersReducedMotion } from '@/hooks/useMediaQuery'
import { useUiStore } from '@/stores/uiStore'
import { Badge } from '@/components/ui/Badge'
import { RelativeTime } from '@/components/ui/RelativeTime'
import { Markdown } from '@/components/chat/Markdown'

type Props = {
  source: Source
  highlighted: boolean
  /** Changes on every "open at this source" request so the flash replays. */
  nonce: number
  cited: boolean
}

export const SourceCard = memo(function SourceCard({ source, highlighted, nonce, cited }: Props) {
  const ref = useRef<HTMLElement>(null)
  const openViewer = useUiStore((s) => s.openViewer)
  const section = source.section
    .split('>')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' › ')

  useEffect(() => {
    if (!highlighted) return
    ref.current?.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' })
  }, [highlighted, nonce])

  return (
    <article
      ref={ref}
      id={`source-${source.id}`}
      aria-label={`Source ${sourceNumber(source.id)}: ${source.title}`}
      className={cn(
        'relative scroll-mt-3 rounded-2xl bg-surface p-4 transition-shadow duration-300',
        highlighted ? 'shadow-[0_0_0_2px_rgb(var(--accent)),0_12px_32px_-12px_rgb(var(--accent)/0.35)]' : 'shadow-card',
      )}
    >
      {highlighted && (
        <span key={nonce} className="pointer-events-none absolute inset-0 rounded-2xl motion-safe:animate-flash" />
      )}
      <header className="flex items-start gap-3">
        <span
          className={cn(
            'mt-0.5 inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1 text-[11px] font-semibold',
            cited ? 'bg-accent text-accent-ink' : 'bg-subtle text-ink-muted',
          )}
          aria-hidden
        >
          {sourceNumber(source.id)}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold leading-5 text-ink">{source.title}</h3>
          {section && <p className="mt-0.5 text-xs leading-5 text-ink-muted">{section}</p>}
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-ink-faint">
            <Badge>{source.source_name}</Badge>
            {source.department && <Badge tone="accent">{source.department}</Badge>}
            <RelativeTime value={source.updated_at} prefix="updated" />
          </div>
        </div>
      </header>

      {source.text && (
        <div
          tabIndex={0}
          role="region"
          aria-label="Section text"
          className="mt-3 max-h-72 overflow-y-auto rounded-xl bg-subtle/50 px-3.5 py-3"
        >
          <Markdown content={source.text} citations={false} compact />
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <button
          type="button"
          onClick={() => openViewer(source.doc_id, source.section)}
          className="group/open inline-flex items-center gap-1.5 rounded-full text-[13px] font-medium text-accent-text"
        >
          <BookOpen
            className="size-3.5 transition-transform duration-300 ease-out group-hover/open:scale-110"
            aria-hidden
          />
          View document
        </button>
        {source.url && (
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="group/ext inline-flex items-center gap-1.5 rounded-full text-[13px] font-medium text-ink-muted hover:text-ink"
          >
            Open source
            <ExternalLink
              className="size-3.5 transition-transform duration-300 ease-out group-hover/ext:-translate-y-0.5 group-hover/ext:translate-x-0.5"
              aria-hidden
            />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
        )}
      </div>
    </article>
  )
})
